import { LayerOptimizationSubjectPositionDto } from '../../creative-unit-package/models/layer/layer-optimization.dto';
import { String } from '../../_core/utils/utils.string';

/**
 * Checks if subject position data is complete and valid.
 * All four properties (sx, sy, sw, sh) must be present and finite.
 *
 * @param subjectPosition - The subject position DTO to validate
 * @returns true if all required properties are present and finite, false otherwise
 *
 * @example
 * const subjectPos = { sx: 10, sy: 20, sw: 50, sh: 40 };
 * hasValidSubjectPosition(subjectPos); // returns true
 *
 * @example
 * const invalidPos = { sx: 10, sy: 20 }; // missing sw and sh
 * hasValidSubjectPosition(invalidPos); // returns false
 */
export function hasValidSubjectPosition(subjectPosition?: LayerOptimizationSubjectPositionDto): boolean {
	if (!subjectPosition) {
		return false;
	}
	if (
		!isFinite(Number(subjectPosition.sh)) || !isFinite(Number(subjectPosition.sw)) ||
		!isFinite(Number(subjectPosition.sy)) || !isFinite(Number(subjectPosition.sx))
	) {
		return false;
	}

	return !(subjectPosition.sh == null || subjectPosition.sw == null || subjectPosition.sy == null || subjectPosition.sx == null);
}

/**
 * Validates that subject position bounds are within acceptable ranges (0-100).
 * Checks that coordinates and dimensions are non-negative, within 0-100 range,
 * and that the bounding box doesn't extend beyond image bounds.
 *
 * @param subjectPosition - The subject position to validate
 * @returns Array of error messages, empty if all validations pass
 *
 * @example
 * const validPos = { sx: 10, sy: 20, sw: 50, sh: 40 };
 * validateSubjectPositionBounds(validPos); // returns []
 *
 * @example
 * const invalidPos = { sx: -5, sy: 20, sw: 50, sh: 40 };
 * validateSubjectPositionBounds(invalidPos);
 * // returns ['sx must be between 0 and 100']
 *
 * @example
 * const extendingPos = { sx: 60, sy: 60, sw: 50, sh: 50 };
 * validateSubjectPositionBounds(extendingPos);
 * // returns ['Subject box extends beyond right edge (sx + sw > 100)', 'Subject box extends beyond bottom edge (sy + sh > 100)']
 */
export function validateSubjectPositionBounds(subjectPosition: LayerOptimizationSubjectPositionDto): string[] {
	const errors: string[] = [];

	// Validate sx (x coordinate)
	const sx = Number(subjectPosition.sx);
	if (sx < 0 || sx > 100 || isNaN(sx) || String.isEmpty(subjectPosition.sx)) {
		errors.push('sx must be between 0 and 100');
	}

	// Validate sy (y coordinate)
	const sy = Number(subjectPosition.sy);
	if (sy < 0 || sy > 100 || isNaN(sy) || String.isEmpty(subjectPosition.sy)) {
		errors.push('sy must be between 0 and 100');
	}

	// Validate sw (width)
	const sw = Number(subjectPosition.sw);
	if (sw <= 0 || sw > 100 || isNaN(sw)) {
		errors.push('sw must be between 0 and 100 (exclusive of 0)');
	}

	// Validate sh (height)
	const sh = Number(subjectPosition.sh);
	if (sh <= 0 || sh > 100 || isNaN(sh)) {
		errors.push('sh must be between 0 and 100 (exclusive of 0)');
	}

	// Check if subject box extends beyond image bounds
	if (sx + sw > 100) {
		errors.push('Subject box extends beyond right edge (sx + sw > 100)');
	}

	if (sy + sh > 100) {
		errors.push('Subject box extends beyond bottom edge (sy + sh > 100)');
	}

	return errors;
}

/**
 * Converts subject position percentages (0-100) to floats (0.0-1.0) for Taskr URLs.
 * Taskr's c_subpos operation expects subject position values as floats between 0.0 and 1.0.
 *
 * @param subjectPosition - The subject position with values in 0-100 range
 * @returns Object with converted values as floats between 0.0-1.0
 *
 * @example
 * const subjectPos = { sx: 25, sy: 50, sw: 40, sh: 30 };
 * convertSubjectPositionToFloats(subjectPos);
 * // returns { sx: 0.25, sy: 0.5, sw: 0.4, sh: 0.3 }
 *
 * @example
 * const edgePos = { sx: 0, sy: 0, sw: 100, sh: 100 };
 * convertSubjectPositionToFloats(edgePos);
 * // returns { sx: 0, sy: 0, sw: 1, sh: 1 }
 */
export function convertSubjectPositionToFloats(subjectPosition: LayerOptimizationSubjectPositionDto): {
	sx: number;
	sy: number;
	sw: number;
	sh: number;
} {
	return {
		sx: Number(subjectPosition.sx) / 100,
		sy: Number(subjectPosition.sy) / 100,
		sw: Number(subjectPosition.sw) / 100,
		sh: Number(subjectPosition.sh) / 100
	};
}
